import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { SITE_LOGO } from '../../constants/assets';
import { motion, AnimatePresence } from 'framer-motion';
import { Estimate } from '../../types/estimate';

const JSPDF_SCRIPT = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
const HTML2CANVAS_SCRIPT = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";

const SEED_ESTIMATE: Estimate = {
  id: '0df1a52c-8067-42f2-841a-85d7b57fa0d2',
  client_name: 'Sra. Elizabeth Ollé',
  client_phone: '607.585353',
  client_email: 'elizabeth.olle@example.com',
  measurements: {
    'Cintura': '88/89 cm'
  },
  items: [
    { description: 'Acortar talle + Ensanchar cintura', price: 112 },
    { description: 'Hombros y sisas', price: 97 },
    { description: 'Acortar mangas', price: 45 }
  ],
  total_amount: 254,
  notes: 'Medidas de cintura: 88/89 cm. Broche en espalda: No requerido.',
  delivery_date: 'Jueves 11/6 Medio día/Tarde',
  status: 'approved',
  created_at: new Date('2026-06-10T16:00:00Z').toISOString()
};

export default function EstimateView() {
  const { id } = useParams<{ id: string }>();
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [loading, setLoading] = useState(true);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [libsReady, setLibsReady] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionSuccess, setActionSuccess] = useState<'approved' | 'rejected' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const estimateRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchEstimate();
    const loadScript = (src: string, scriptId: string) => {
      return new Promise((resolve) => {
        if (document.getElementById(scriptId)) { resolve(true); return; }
        const script = document.createElement('script');
        script.id = scriptId;
        script.src = src;
        script.onload = () => resolve(true);
        document.body.appendChild(script);
      });
    };
    const init = async () => {
      await loadScript(HTML2CANVAS_SCRIPT, 'canvas-lib');
      await loadScript(JSPDF_SCRIPT, 'pdf-lib');
      setLibsReady(true);
    };
    init();
  }, [id]);

  const fetchEstimate = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);

    const getSeedIfMatches = (targetId: string) => {
      if (targetId === '0df1a52c-8067-42f2-841a-85d7b57fa0d2') {
        const localData = localStorage.getItem('admin_estimates');
        const list = localData ? JSON.parse(localData) as Estimate[] : [];
        if (!list.some(e => e.id === targetId)) {
          list.push(SEED_ESTIMATE);
          localStorage.setItem('admin_estimates', JSON.stringify(list));
        }
        return SEED_ESTIMATE;
      }
      return null;
    };

    try {
      // Intentar cargar de Supabase
      const { data, error: dbError } = await supabase
        .from('estimates')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (dbError) throw dbError;

      if (data) {
        setEstimate(data);
      } else {
        // Fallback a localStorage
        const localData = localStorage.getItem('admin_estimates');
        let found: Estimate | null = null;
        if (localData) {
          const list = JSON.parse(localData) as Estimate[];
          found = list.find(e => e.id === id) || null;
        }

        if (!found) {
          found = getSeedIfMatches(id);
        }

        if (found) {
          setEstimate(found);
        } else {
          setError("Presupuesto no encontrado.");
        }
      }
    } catch (err: any) {
      console.warn("Supabase fetch error, falling back to localStorage:", err);
      // Fallback a localStorage en caso de error
      const localData = localStorage.getItem('admin_estimates');
      let found: Estimate | null = null;
      if (localData) {
        const list = JSON.parse(localData) as Estimate[];
        found = list.find(e => e.id === id) || null;
      }

      if (!found) {
        found = getSeedIfMatches(id);
      }

      if (found) {
        setEstimate(found);
      } else {
        setError("Error al cargar el presupuesto: " + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (newStatus: 'approved' | 'rejected') => {
    if (!estimate || actionLoading) return;
    setActionLoading(true);
    setError(null);
    try {
      // 1. Intentar actualizar en Supabase
      const { error: dbError } = await supabase
        .from('estimates')
        .update({ status: newStatus })
        .eq('id', estimate.id);

      // Si da error de tabla no existente o similar, procedemos directamente a local
      if (dbError) throw dbError;

      // Actualización exitosa en Supabase, refrescar localmente
      setEstimate(prev => prev ? { ...prev, status: newStatus } : null);
      setActionSuccess(newStatus);
      setTimeout(() => setActionSuccess(null), 3000);
    } catch (err: any) {
      console.warn("Supabase update failed, updating locally in localStorage:", err);
      // Fallback a localStorage
      const localData = localStorage.getItem('admin_estimates');
      if (localData) {
        const list = JSON.parse(localData) as Estimate[];
        const updatedList = list.map(e => e.id === estimate.id ? { ...e, status: newStatus } : e);
        localStorage.setItem('admin_estimates', JSON.stringify(updatedList));
        
        setEstimate(prev => prev ? { ...prev, status: newStatus } : null);
        setActionSuccess(newStatus);
        setTimeout(() => setActionSuccess(null), 3000);
      } else {
        setError("Error al actualizar el presupuesto.");
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!estimateRef.current || !libsReady || pdfGenerating || !estimate) return;
    setPdfGenerating(true);
    try {
      const element = estimateRef.current;
      const { jsPDF } = (window as any).jspdf;

      const PAGE_W_MM = 210;
      const PAGE_H_MM = 297;
      // Cap the render scale so we don't produce an oversized bitmap; still
      // sharp for text/print.
      const SCALE = Math.min(2, window.devicePixelRatio || 1.5) || 2;

      const canvas = await (window as any).html2canvas(element, {
        scale: SCALE,
        useCORS: true,
        backgroundColor: '#FFFFFF',
        logging: false,
      });

      const pageHeightPx = Math.floor((canvas.width * PAGE_H_MM) / PAGE_W_MM);
      const totalPages = Math.ceil(canvas.height / pageHeightPx);

      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });

      for (let page = 0; page < totalPages; page++) {
        if (page > 0) pdf.addPage();

        const srcY = page * pageHeightPx;
        const srcH = Math.min(pageHeightPx, canvas.height - srcY);

        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = pageHeightPx;
        const ctx = pageCanvas.getContext('2d')!;
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        ctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);

        const imgData = pageCanvas.toDataURL('image/jpeg', 0.9);
        pdf.addImage(imgData, 'JPEG', 0, 0, PAGE_W_MM, PAGE_H_MM, undefined, 'FAST');
      }

      const clientClean = estimate.client_name.replace(/\s+/g, '_');
      pdf.save(`Presupuesto_${clientClean}_${estimate.id.split('-')[0].toUpperCase()}.pdf`);
    } catch (pdfErr) {
      console.error("PDF Generation failed:", pdfErr);
      setError("No se pudo generar el PDF. Por favor, imprime la página directamente.");
    } finally {
      setPdfGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line text-3xl text-brand-black animate-spin" />
          <p className="font-sans text-xs uppercase tracking-widest text-brand-gray-400">Cargando Presupuesto...</p>
        </div>
      </div>
    );
  }

  if (error || !estimate) {
    return (
      <div className="min-h-screen bg-brand-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl border border-brand-gray-200 p-8 max-w-md w-full text-center space-y-4 shadow-xl">
          <i className="ri-error-warning-line text-4xl text-red-500" />
          <h2 className="font-serif text-xl text-brand-black">Presupuesto no encontrado</h2>
          <p className="font-sans text-brand-gray-500 text-sm">{error || "El enlace no es válido o ha expirado."}</p>
          <button onClick={() => window.history.back()} className="mt-4 bg-brand-black text-white px-6 py-2.5 rounded-full text-xs font-sans uppercase tracking-widest hover:bg-brand-gray-800 transition-all">
            Volver
          </button>
        </div>
      </div>
    );
  }

  // Desglose de precios
  const subtotal = estimate.items.reduce((sum, item) => sum + item.price, 0);
  const vat = subtotal * 0.21;
  const total = subtotal + vat;

  // Estados formateados
  const statusLabels = {
    draft: { label: 'Borrador', style: 'bg-brand-gray-100 text-brand-gray-500' },
    sent: { label: 'Pendiente de Aprobación', style: 'bg-amber-50 border border-amber-200 text-amber-700' },
    approved: { label: 'Presupuesto Aprobado', style: 'bg-brand-sage/20 border border-brand-sage/30 text-brand-black font-bold' },
    rejected: { label: 'Presupuesto Rechazado', style: 'bg-red-50 border border-red-200 text-red-700' },
    completed: { label: 'Servicio Completado', style: 'bg-blue-50 border border-blue-200 text-blue-700' }
  };

  const currentStatus = statusLabels[estimate.status] || { label: estimate.status, style: 'bg-brand-gray-100 text-brand-gray-500' };

  return (
    <div className="min-h-screen bg-[#FDFDFD] py-12 px-6 font-sans">
      <div className="max-w-[900px] mx-auto">
        {/* elegant Bar de Acciones */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-center mb-8 no-print border-b border-brand-gray-100 pb-6">
          <div className="flex items-center gap-4">
            <span className="text-[10px] uppercase tracking-[0.3em] text-brand-gray-400 font-medium">Anthony Sánchez Couture / Presupuesto Oficial</span>
            <span className={`px-3 py-1 rounded-full text-[9px] uppercase tracking-wider font-medium ${currentStatus.style}`}>
              {currentStatus.label}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => window.print()} className="text-[10px] uppercase tracking-[0.2em] px-5 py-2.5 border border-brand-gray-200 rounded-full hover:bg-brand-gray-50 transition-all font-bold">
              Imprimir
            </button>
            <button onClick={handleDownloadPDF} disabled={pdfGenerating} className="text-[10px] uppercase tracking-[0.2em] px-6 py-2.5 bg-black text-white rounded-full hover:bg-brand-gray-800 transition-all disabled:opacity-30 font-bold">
              {pdfGenerating ? 'Procesando...' : 'Descargar PDF'}
            </button>
          </div>
        </div>

        {/* Notificaciones de Acción */}
        <AnimatePresence>
          {actionSuccess && (
            <motion.div
              initial={{ opacity: 0, y: -15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              className={`mb-6 p-4 rounded-2xl text-center font-sans text-sm font-medium border ${
                actionSuccess === 'approved'
                  ? 'bg-brand-sage/15 border-brand-sage/30 text-brand-black'
                  : 'bg-red-50 border-red-100 text-red-700'
              }`}
            >
              {actionSuccess === 'approved'
                ? '¡Presupuesto aprobado correctamente! Muchas gracias. Nos pondremos en contacto pronto.'
                : 'Presupuesto marcado como rechazado.'}
            </motion.div>
          )}
        </AnimatePresence>

        {/* COMPACT MINIMALIST LUXURY ESTIMATE */}
        <div className="w-full overflow-x-auto pb-8 -mx-6 px-6 md:mx-0 md:px-0 scrollbar-hide">
          <div 
            ref={estimateRef} 
            className="bg-white relative p-12 md:p-16 border border-brand-gray-50 shadow-2xl mx-auto text-left"
            style={{ width: '840px', minWidth: '840px', minHeight: '1100px' }}
          >
            {/* Header Section */}
            <div className="flex justify-between items-start mb-12 relative z-10">
              <div className="space-y-8">
                <img 
                  src={SITE_LOGO} 
                  alt="Anthony Sánchez" className="h-8 w-auto opacity-90"
                />
                <div className="space-y-0.5 text-[8px] uppercase tracking-[0.15em] text-brand-gray-400 font-medium">
                  <p className="text-black font-bold">Anthony Sánchez Couture</p>
                  <p>Mestre Nicolau, 19, 08021 Barcelona</p>
                  <p>NIF: 46146123Z <span className="mx-1">|</span> Tel: +34 625 46 34 20</p>
                  <p>Email: anthosanchez3108@gmail.com</p>
                </div>
              </div>
              <div className="text-right space-y-8">
                <div className="space-y-1">
                  <h1 className="text-[9px] uppercase tracking-[0.4em] text-brand-gray-300 font-light">Presupuesto de Atelier</h1>
                  <p className="text-lg font-light tracking-[0.05em] text-black italic font-serif">Ref: ESP-{estimate.id.split('-')[0].toUpperCase()}</p>
                </div>
                <div className="text-[8px] uppercase tracking-[0.15em] text-brand-gray-400 space-y-0.5">
                  <p>Fecha de elaboración</p>
                  <p className="text-black font-bold">{new Date(estimate.created_at).toLocaleDateString('es-ES')}</p>
                </div>
              </div>
            </div>

            {/* Client & Delivery Information */}
            <div className="grid grid-cols-2 gap-12 mb-12 border-t border-brand-gray-100 pt-8 relative z-10">
              <div className="space-y-3">
                <span className="text-[7px] uppercase tracking-[0.3em] text-brand-gray-300 font-bold">Datos del Cliente</span>
                <div className="space-y-1">
                  <p className="text-base font-serif italic text-black leading-tight">{estimate.client_name}</p>
                  <div className="space-y-0.5 text-[8px] uppercase tracking-[0.1em] text-brand-gray-500 font-medium">
                    {estimate.client_phone && <p suppressHydrationWarning>Teléfono: {estimate.client_phone}</p>}
                    {estimate.client_email && <p suppressHydrationWarning>Email: {estimate.client_email}</p>}
                  </div>
                </div>
              </div>
              <div className="text-right space-y-3">
                <span className="text-[7px] uppercase tracking-[0.3em] text-brand-gray-300 font-bold">Detalles del Encargo</span>
                <div className="space-y-1.5">
                  {estimate.delivery_date && (
                    <div>
                      <p className="text-[8px] uppercase tracking-[0.15em] text-brand-gray-400">Entrega Estimada</p>
                      <p className="text-xs font-serif font-light text-black tracking-tight mt-0.5">{estimate.delivery_date}</p>
                    </div>
                  )}
                  {estimate.measurements && Object.keys(estimate.measurements).length > 0 && (
                    <div className="pt-1">
                      <p className="text-[8px] uppercase tracking-[0.15em] text-brand-gray-400 mb-0.5">Medidas Registradas</p>
                      <div className="text-[9px] font-mono text-brand-black flex flex-col items-end gap-0.5">
                        {Object.entries(estimate.measurements).map(([k, v]) => (
                          <span key={k}>{k}: <strong className="font-bold">{v}</strong></span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Table Section */}
            <div className="relative z-10 min-h-[350px]">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-black">
                    <th className="pb-4 text-[7px] uppercase tracking-[0.3em] text-brand-gray-400 font-bold">Descripción del Trabajo Artesanal</th>
                    <th className="pb-4 text-[7px] uppercase tracking-[0.3em] text-brand-gray-400 font-bold text-right w-24">Precio</th>
                  </tr>
                </thead>
                <tbody>
                  {estimate.items.map((item, idx) => (
                    <tr key={idx} className="border-b border-brand-gray-50">
                      <td className="py-5">
                        <p className="text-[11px] text-black font-medium tracking-wide">{item.description}</p>
                        <p className="text-[7px] uppercase tracking-[0.15em] text-brand-gray-300 mt-1 italic font-light font-serif">Atelier de Alta Costura - Mano de obra</p>
                      </td>
                      <td className="py-5 text-[12px] font-serif font-light text-black text-right tracking-tight">
                        {item.price.toFixed(2)}€
                      </td>
                    </tr>
                  ))}
                  {estimate.items.length === 0 && (
                    <tr>
                      <td colSpan={2} className="py-10 text-center font-sans text-sm text-brand-gray-400 italic">
                        No hay conceptos detallados en este presupuesto.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Totals & Notes */}
            <div className="mt-12 flex justify-between items-start gap-12 relative z-10 pt-10 border-t border-brand-gray-100">
              <div className="max-w-[320px] space-y-6">
                {estimate.notes && (
                  <div className="space-y-2">
                    <span className="text-[7px] uppercase tracking-[0.3em] text-brand-gray-300 font-bold">Observaciones del Atelier</span>
                    <p className="text-[10px] text-brand-gray-600 leading-relaxed font-serif italic bg-brand-cream/10 p-4 border border-brand-gray-100 rounded-xl">
                      "{estimate.notes}"
                    </p>
                  </div>
                )}
                
                <div className="space-y-1.5 text-[8px] text-brand-gray-400 leading-relaxed">
                  <p className="font-bold uppercase tracking-wider text-black">Validez y Condiciones</p>
                  <p>• Este presupuesto tiene una validez de 30 días a partir de su emisión.</p>
                  <p>• Los arreglos de alta costura se realizan bajo cita previa y pruebas presenciales.</p>
                  <p>• El atelier garantiza un encaje artesanal óptimo según las medidas registradas.</p>
                </div>
              </div>
              
              <div className="w-64 space-y-3">
                <div className="flex justify-between text-[8px] uppercase tracking-[0.15em] text-brand-gray-400 font-medium">
                  <span>Base Imponible</span>
                  <span className="text-black">{subtotal.toFixed(2)}€</span>
                </div>
                <div className="flex justify-between text-[8px] uppercase tracking-[0.15em] text-brand-gray-400 font-medium pb-2 border-b border-brand-gray-50">
                  <span>IVA (21%)</span>
                  <span className="text-black">{vat.toFixed(2)}€</span>
                </div>
                <div className="flex justify-between items-end pt-2 border-b border-brand-gray-100 pb-4">
                  <span className="text-[9px] uppercase tracking-[0.2em] text-brand-gray-300 italic font-serif">Total Presupuestado</span>
                  <span className="text-3xl font-serif font-light text-black tracking-tighter">{total.toFixed(2)}€</span>
                </div>

                {/* Firma del Cliente o Atelier */}
                <div className="pt-6 space-y-4">
                  <p className="text-[7px] uppercase tracking-[0.3em] text-brand-gray-300 font-bold">Firma de Conformidad</p>
                  <div className="h-14 border border-dashed border-brand-gray-200 rounded-xl flex items-center justify-center bg-[#FAFAFA]">
                    {estimate.status === 'approved' ? (
                      <span className="text-[10px] uppercase tracking-widest text-brand-sage font-bold flex items-center gap-1.5">
                        <i className="ri-checkbox-circle-fill text-sm" /> APROBADO DIGITALMENTE
                      </span>
                    ) : estimate.status === 'rejected' ? (
                      <span className="text-[10px] uppercase tracking-widest text-red-500 font-bold flex items-center gap-1.5">
                        <i className="ri-close-circle-fill text-sm" /> RECHAZADO
                      </span>
                    ) : (
                      <span className="text-[8px] uppercase tracking-wider text-brand-gray-400 font-light italic">
                        Pendiente de firma
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Luxury Footer */}
            <div className="mt-24 text-center space-y-3 relative z-10 opacity-50">
              <div className="flex items-center justify-center gap-3">
                <div className="h-[0.5px] w-8 bg-brand-gray-200" />
                <p className="text-[8px] uppercase tracking-[0.4em] text-black font-light italic font-serif">Anthony Sánchez</p>
                <div className="h-[0.5px] w-8 bg-brand-gray-200" />
              </div>
              <p className="text-[7px] uppercase tracking-[0.3em] text-brand-gray-400 text-center">Alta Costura • Novias • Trajes a Medida</p>
            </div>
          </div>
        </div>

        {/* Client Interactive Approval Box - only shown if status is sent or draft */}
        {estimate.status !== 'approved' && estimate.status !== 'completed' && estimate.status !== 'rejected' && (
          <div className="no-print mt-8 p-6 bg-brand-cream/30 border border-brand-sage/20 rounded-3xl flex flex-col sm:flex-row items-center justify-between gap-6 shadow-xl shadow-brand-sage/5">
            <div className="text-left">
              <h3 className="font-serif text-lg text-brand-black">¿Desea dar su conformidad?</h3>
              <p className="font-sans text-xs text-brand-gray-500 mt-1">Al aprobar el presupuesto, nos autoriza a iniciar las modificaciones artesanas de su prenda.</p>
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <button
                onClick={() => handleUpdateStatus('rejected')}
                disabled={actionLoading}
                className="flex-1 sm:flex-initial text-[10px] uppercase tracking-[0.2em] px-6 py-3 border border-red-200 text-red-600 rounded-full hover:bg-red-50 transition-all font-bold disabled:opacity-50"
              >
                Rechazar
              </button>
              <button
                onClick={() => handleUpdateStatus('approved')}
                disabled={actionLoading}
                className="flex-2 sm:flex-initial text-[10px] uppercase tracking-[0.2em] px-8 py-3.5 bg-brand-black text-white rounded-full hover:bg-brand-gray-800 transition-all font-bold disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {actionLoading && <i className="ri-loader-4-line animate-spin" />}
                Aprobar y Confirmar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
